/** @file Hook: canvas pointer handlers that run the curve pointer machine and carry out its effects. */
import { useMemo, type PointerEvent, type RefObject } from 'react';
import { applyCurveEffect } from './applyCurveEffect';
import { curvePointer, type CurvePointerContext, type CurvePointerInput, type CurveStep } from './curvePointer';
import type { CurvePorts } from './curvePorts';
import { curveViewStore } from './curveViewStore';
import type { CurveModel } from './useCurveModel';

/** React pointer props for the graph canvas. */
export interface CurvePointerHandlers {
  onPointerDown: (e: PointerEvent<HTMLCanvasElement>) => void;
  onPointerMove: (e: PointerEvent<HTMLCanvasElement>) => void;
  onPointerUp: (e: PointerEvent<HTMLCanvasElement>) => void;
  onPointerCancel: () => void;
}

function inputOf(e: PointerEvent<HTMLCanvasElement>): CurvePointerInput {
  const r = e.currentTarget.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top, button: e.button, shift: e.shiftKey, alt: e.altKey };
}

/** Picks against the last painted frame (`last`); the gesture lives in curveViewStore, never in React state. */
export function useCurvePointer(last: RefObject<CurvePointerContext | null>, model: CurveModel, ports: CurvePorts): CurvePointerHandlers {
  return useMemo(() => {
    const run = (step: CurveStep): void => {
      curveViewStore.setState({ gesture: step.gesture });
      for (const effect of step.effects) applyCurveEffect(effect, ports, model.refs);
    };
    return {
      onPointerDown: (e) => {
        if (last.current === null) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        run(curvePointer.down(last.current, inputOf(e)));
      },
      onPointerMove: (e) => {
        const gesture = curveViewStore.getState().gesture;
        if (last.current === null || gesture.kind === 'idle') return;
        run(curvePointer.move(last.current, gesture, inputOf(e)));
      },
      onPointerUp: () => {
        if (last.current !== null) run(curvePointer.up(last.current, curveViewStore.getState().gesture));
      },
      onPointerCancel: () => { curveViewStore.setState({ gesture: { kind: 'idle' } }); },
    };
  }, [last, model, ports]);
}
